import { useState, useEffect } from 'react';
import { Users, Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { postRequest } from "../utils/networking";
import { apiUrls } from "../utils/url";
import Pagination from '../components/Pagination';

export default function CourseUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await postRequest(apiUrls.getallcousers, {});
      if (response && response.status === 'S' && response.data) {
        setUsers(response.data);
      } else {
        toast.error(response?.message || "Failed to fetch users");
      }
    } catch (err) {
      toast.error("Network error while fetching users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(u => 
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  return (
    <div className="h-full flex flex-col bg-slate-50 p-6 overflow-hidden">
      <div className="flex justify-start items-center mb-6 shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input 
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-orange-500 w-64 bg-white"
            />
          </div>

        </div>
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto flex-1 custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead className="sticky top-0 bg-slate-50/95 backdrop-blur-sm shadow-sm z-10">
              <tr className="border-b border-slate-200 text-slate-600 text-sm">
                <th className="font-semibold py-3 px-6 w-16">S.No</th>
                <th className="font-semibold py-3 px-6">User Name</th>
                <th className="font-semibold py-3 px-6">Email Address</th>
                <th className="font-semibold py-3 px-6">Joined On</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
                      <p>Loading users...</p>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-12 text-slate-500">
                    No users found matching your search.
                  </td>
                </tr>
              ) : (
                currentItems.map((user, idx) => (
                  <tr key={user.userid} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-6 text-sm text-slate-500">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="py-3 px-6">
                      <span className="font-medium text-slate-800">{user.username || 'Unknown'}</span>
                    </td>
                    <td className="py-3 px-6 text-sm text-slate-600">{user.email}</td>
                    <td className="py-3 px-6 text-sm text-slate-500">{user.createdon || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="border-t border-slate-200 p-4 shrink-0 bg-white">
            <Pagination 
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}
